import axios from 'axios'

import { Env, getEnv } from '@cinch/oxd-utils/env/src'
import { queryString } from '@cinch/oxd-utils/requests/src'

const INVENTORY_SERVICE_URL = getEnv(Env.InventoryServiceUrl)
const vehicle = axios.create({ baseURL: getEnv(Env.VehicleServiceUrl) })

export interface InventoryVehicleData {
  vehicleInventoryId: string
  vin: string
  vrm: string
  vehicleId: string
  capId: number | null
  make: string
  model: string
  modelYear: number | null
  variant: string
  derivative: string
  registrationDate: string
  mileage: number
  registeredKeepers: number
  colour: string
  vatSchemeCode: string
  valuationWholesaleGOne: number | null
  valuationWholesaleGOneThirtyDays: number | null
  valuationWholesaleGOneSixtyDays: number | null
  valuationWholesaleGOneNinetyDays: number | null
  valuationRetail: number | null
  serviceHistory: boolean | string | null
  vehicleType: string
  fuelType: string
  bodyType: string
  transmissionType: string
  engineCapacityCc: number
  doors: number
  seats: number
  length: number | null
  wheelbase: number | null
  width: number | null
  height: number | null
  urbanMpg: number | null
  extraUrbanMpg: number | null
  combinedMpg: number | null
  serviceIntervalMiles: number | null
  serviceIntervalMonths: number | null
  zeroToSixtyTwo: number | null
  topSpeed: number | null
  bhp: number | null
  fuelTankCapacityLitres: number | null
  towingWeightBraked: number | null
  towingWeightUnbraked: number | null
  co2: number | null
  luggageCapacitySeatsDown: number | null
  luggageCapacitySeatsUp: number | null
  emissionsStandard: string
  insuranceGroupOneToFifty: string
  ncapRating: string
  grossWeightKg: number | null
  maxRoofLoadKg: number | null
  price: number
  createdDate: string
  status: string
  published: boolean
  ulez: boolean | null
  media?: Cinch.AutosOnShowResponse
}

export const getAutosOnShowMedia = async (vehicleReg: string) => {
  return await axios.get(`${INVENTORY_SERVICE_URL}/media/${vehicleReg}`)
}

export const getDirectVehicleDetails = (vehicleInventoryId: string) => {
  return vehicle.request<InventoryVehicleData>({
    url: `${INVENTORY_SERVICE_URL}/vehicle/${vehicleInventoryId}`,
    method: 'GET',
  })
}

export const getVehicleSummary = (vehicleId: number) => {
  return vehicle.request<Cinch.VehicleFavouriteSummary>({
    url: `/summaries/${vehicleId}`,
    method: 'GET',
  })
}

export const getVehicleDetails = (vehicleId: number, location?: Partial<Cinch.UserProfileRequest>) => {
  return vehicle.request<Cinch.VehicleDetails>({
    url: location
      ? `/vehicles/${vehicleId}?latitude=${location.latitude}&longitude=${location.longitude}`
      : `/vehicles/${vehicleId}`,
    method: 'GET',
  })
}

interface SendEmailRequest {
  vehicleId: number
  firstName: string
  lastName: string
  phoneNumber: string
  userEmail: string
  message: string
}

export interface Car {
  manufacturerName: string
  modelName?: string
}

export const sendEmail = ({ vehicleId, ...body }: SendEmailRequest) => {
  return vehicle.request({
    url: `/vehicles/${vehicleId}/email`,
    method: 'POST',
    data: { ...body },
  })
}

export const sendCallbackRequest = ({
  vehicleId,
  firstName,
  phoneNumber,
  preferredTimeSlot,
}: {
  vehicleId: number
  firstName: string
  phoneNumber: string
  preferredTimeSlot: string
}) => {
  return vehicle.request({
    url: `/vehicles/${vehicleId}/callback`,
    method: 'POST',
    data: { firstName, phoneNumber, preferredTimeSlot },
  })
}

export const getVehicles = (car: Car, pageSize = 6) => {
  const defaultParams = {
    dealershipStatus: 'Live',
    fromPrice: -1,
    toPrice: -1,
    fromYear: -1,
    toYear: -1,
    mileage: -1,
    fromEngineSize: -1,
    toEngineSize: -1,
    sortingCriteria: 1,
    longitude: '',
    latitude: '',
    distance: 0,
    bodyType: '',
    colour: '',
    doors: '',
    fuelType: '',
    keywords: '',
    make: '',
    selectedModel: '',
    transmissionType: '',
    query: '',
  }
  const { manufacturerName, modelName = '' } = car
  const params = {
    ...defaultParams,
    make: manufacturerName,
    selectedModel: modelName,
    pageSize,
  }

  return vehicle.request({
    url: `vehicles?${queryString(params)}`,
    method: 'GET',
  })
}
